import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import useApiClient from '../../../services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateArtEventRequest,
  CreateArtEventResponse,
  GetArtEventsApiResponse,
} from '../../../interfaces/artEvent/ArtEventInterfaces';
import { ApiError } from '../../../interfaces/ApiError';
import { createArtEvent, getArtEvents } from '../../../services/artEvent/artEventService';
import { ArtEventEndpointQueryKeys } from '../../../constants/artEvent/endpoints';
import { format, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { setToLocalStorage } from '../../../utils/localStorage';
import { ART_EVENT_ID_LOCAL_STORAGE_KEY } from '../../../constants/artEvent/artEventConstants';
import { URLS } from '../../../constants/url';
import { DEFAULT_ART_EVENT_WAITLIST } from '../../../../EditArtEvent/constants/artEventWaitlist';

export const MAX_ART_EVENT_NAME_LENGTH = 50;
export const DATE_DISPLAY_FORMAT = 'MMM d, y';

export const useCreateArtEventForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const { data: artEventsData } = useQuery<GetArtEventsApiResponse, ApiError>({
    queryKey: [ArtEventEndpointQueryKeys.GET_ART_EVENTS],
    queryFn: () => getArtEvents(api),
  });
  // No event id exists yet on the create form, so show the opt-in only when the Weblab resolves
  // on for every event (a broad/100% dial) — otherwise the backend would reject enabling the
  // waitlist on a not-yet-dialled new event.
  const events = artEventsData?.artEvents ?? [];
  const waitlistFeatureEnabled =
    events.length > 0 && events.every((e) => e.features?.waitlist === true);

  const defaultStartDate = new Date();
  defaultStartDate.setHours(0, 0, 0, 0); // it considers time from midnight

  const [artEventName, setArtEventName] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [activeStartDatePicker, setActiveStartDatePicker] =
    useState<boolean>(false);
  const [activeEndDatePicker, setActiveEndDatePicker] =
    useState<boolean>(false);
  const [selectedStart, setSelectedStart] = useState<string>(t('today'));
  const [selectedEnd, setSelectedEnd] = useState<string>(t('select-end-date'));
  const [showError, setShowError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  // Waitlist is opt-in and off by default; creating a standard Art Event
  // simply leaves this untouched.
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{
    artEventName?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  const anchorRefStart = useRef<HTMLInputElement>(null);
  const anchorRefEnd = useRef<HTMLInputElement>(null);

  const createArtEventMutation = useMutation<
    CreateArtEventResponse,
    ApiError,
    CreateArtEventRequest
  >({
    mutationFn: (artEventData: CreateArtEventRequest) =>
      createArtEvent(api, artEventData),
    onSuccess: /* istanbul ignore next -- React Query invokes asynchronously; covered by integration tests */
    (artEventData: CreateArtEventResponse) => {
      queryClient.invalidateQueries({
        queryKey: [ArtEventEndpointQueryKeys.GET_ART_EVENTS],
      });
      setToLocalStorage(
        ART_EVENT_ID_LOCAL_STORAGE_KEY,
        artEventData.artEventId
      );

      setArtEventName('');
      setStartDate(defaultStartDate);
      setEndDate(undefined);
      setWaitlistEnabled(false);
      setShowSuccess(true);
      setSelectedStart(t('today'));
      setSelectedEnd(t('select-end-date'));
    },
    onError: /* istanbul ignore next */
    () => {
      setShowError(true);
    },
  });

  const handleArtEventNameChange = (value: string) => {
    if (value.length <= MAX_ART_EVENT_NAME_LENGTH) {
      setArtEventName(value);
      if (validationErrors.artEventName)
        setValidationErrors((prev) => ({ ...prev, artEventName: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};
    let isValid = true;

    // Validate art event name
    if (!artEventName.trim()) {
      errors.artEventName = t('art-event-name-required');
      isValid = false;
    }

    // Validate start date (always set from useState — defensive guard)
    /* istanbul ignore next */
    if (!startDate) {
      errors.startDate = t('start-date-required');
      isValid = false;
    }

    // Validate end date
    if (!endDate) {
      errors.endDate = t('end-date-required');
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm() && endDate) {
      setShowError(false);
      setShowSuccess(false);
      const artEventData: CreateArtEventRequest = {
        artEvent: {
          artEventID: artEventName,
          startTimestamp: startDate.getTime(),
          endTimestamp: endDate.getTime(),
          // Send true when opting into the waitlist, else false (standard).
          waitlistEnabled: waitlistEnabled ? true : DEFAULT_ART_EVENT_WAITLIST,
        },
      };
      createArtEventMutation.mutate(artEventData);
    }
  };

  const handleCancel = () => navigate(URLS.ART_EVENT);

  const handleDateSelect = (date: Date, isStartDate: boolean) => {
    const formattedDate = format(date, DATE_DISPLAY_FORMAT);

    if (isStartDate) {
      setSelectedStart(formattedDate);
      setStartDate(date);
      if (endDate && isAfter(date, endDate)) {
        setEndDate(undefined);
        setSelectedEnd(t('select-end-date'));
      }
      setActiveStartDatePicker(false);
      /* istanbul ignore next -- startDate is always set; this clears an unreachable validation error */
      if (validationErrors.startDate)
        setValidationErrors((prev) => ({ ...prev, startDate: undefined }));
    } else {
      setSelectedEnd(formattedDate);
      setEndDate(date);
      setActiveEndDatePicker(false);
      if (validationErrors.endDate)
        setValidationErrors((prev) => ({ ...prev, endDate: undefined }));
    }
  };

  return {
    errorProps: { showError, setShowError },
    successProps: { showSuccess, setShowSuccess },
    artEventProps: { artEventName, handleArtEventNameChange },
    dateProps: {
      startDate,
      endDate,
      activeStartDatePicker,
      activeEndDatePicker,
      selectedStart,
      selectedEnd,
      anchorRefStart,
      anchorRefEnd,
      setActiveStartDatePicker,
      setActiveEndDatePicker,
      handleDateSelect,
    },
    mutationProps: { createArtEventMutation },
    buttonProps: { handleSubmit, handleCancel },
    validationProps: { validationErrors },
    waitlistProps: { waitlistEnabled, setWaitlistEnabled, waitlistFeatureEnabled },
  };
};
